import { useState } from 'react';
import {
  BookOpen,
  Check,
  Globe,
  KeyRound,
  Lock,
  MoreHorizontal,
} from 'lucide-react';
import { Button } from '../button/button';
import { cn } from '../../../lib/utils';
import type { WorkspaceKeyStatus } from '../../../lib/managed-agents/types';
import { useConnectionRequests } from '../../../lib/hooks/useConnectionRequests';
import { CARD_CLASS } from './constants';
import {
  ConnectionGuideDialog,
  type GuideId,
} from './guides/connection-guides';
import { PlatformRequestForm } from './connection-requests';
import { StatusPill } from './status-pill';

interface AgentHomeCardsProps {
  workspaceId: string;
  keyStatus: WorkspaceKeyStatus | null;
  onImport: () => void;
  onConnectEndpoint: () => void;
}

/** "Where does your agent live?": one card per place an agent can come from. */
export function AgentHomeCards({
  workspaceId,
  keyStatus,
  onImport,
  onConnectEndpoint,
}: AgentHomeCardsProps) {
  const { requested, request } = useConnectionRequests(workspaceId);
  const [guide, setGuide] = useState<GuideId | null>(null);

  return (
    <section
      className="flex flex-col gap-4"
      aria-labelledby="homes-heading"
      data-onboarding="connections-homes"
    >
      <h2
        id="homes-heading"
        className="text-base font-semibold text-prompt-safecomvulcan"
      >
        Where does your agent live?
      </h2>

      <div className="grid gap-4 lg:grid-cols-3">
        <HomeCard
          mark={<span className="text-base font-semibold">A</span>}
          markClassName="border-[#1F1F1F] bg-[#1F1F1F] text-white"
          title="Claude Managed Agents"
          subtitle="by Anthropic"
          aside={<GuideLink onClick={() => setGuide('claude')} />}
        >
          <div className="flex flex-wrap items-center justify-between gap-3">
            {keyStatus?.connected ? (
              <StatusPill tone="ok" dot={false}>
                <Check className="h-3.5 w-3.5" />I have an API key
              </StatusPill>
            ) : (
              <StatusPill dot={false}>
                <KeyRound className="h-3.5 w-3.5" />
                Needs an API key
              </StatusPill>
            )}
            <Button
              onClick={onImport}
              className="hover:bg-prompt-safecomoutrageous-orange/90 bg-prompt-safecomoutrageous-orange text-white"
            >
              Import an agent
            </Button>
          </div>
        </HomeCard>

        <HomeCard
          mark={<Globe className="h-5 w-5" />}
          title="Your own API"
          subtitle="Any bot behind an HTTPS endpoint"
          aside={<GuideLink onClick={() => setGuide('json')} />}
        >
          <div className="flex flex-wrap items-center justify-between gap-3">
            <span className="text-xs text-prompt-safecomabbey">
              JSON, OpenAI or Anthropic format
            </span>
            <Button
              onClick={onConnectEndpoint}
              className="hover:bg-prompt-safecomoutrageous-orange/90 bg-prompt-safecomoutrageous-orange text-white"
            >
              Connect an endpoint
            </Button>
          </div>
        </HomeCard>

        <HomeCard
          muted
          mark={<MoreHorizontal className="h-5 w-5" />}
          title="Other platforms"
          subtitle="Bedrock, Vertex AI, OpenAI…"
        >
          <PlatformRequestForm requested={requested} onRequest={request} />
        </HomeCard>
      </div>

      {guide && (
        <ConnectionGuideDialog
          open
          onOpenChange={(open) => !open && setGuide(null)}
          initialGuide={guide}
        />
      )}

      <p className="flex items-center gap-2 text-sm text-prompt-safecomabbey">
        <Lock className="h-4 w-4 flex-none text-prompt-safecomoutrageous-orange" />
        Your agent's prompt stays on your side. PromptSafe only sends messages
        and reads replies.
      </p>
      <p className="text-sm text-prompt-safecomabbey">
        Simulations with a connected agent use PS tokens for the simulated user
        and the evaluations. Your agent's replies are not charged.
      </p>
    </section>
  );
}

interface HomeCardProps {
  mark: React.ReactNode;
  markClassName?: string;
  title: string;
  subtitle: string;
  aside?: React.ReactNode;
  muted?: boolean;
  children: React.ReactNode;
}

function HomeCard({
  mark,
  markClassName,
  title,
  subtitle,
  aside,
  muted = false,
  children,
}: HomeCardProps) {
  return (
    <div
      className={cn(
        'flex flex-col justify-between gap-5 p-5',
        muted
          ? 'rounded-[10px] border border-dashed border-[#00000030]'
          : CARD_CLASS,
      )}
    >
      <div className="flex items-start gap-3">
        <div
          aria-hidden="true"
          className={cn(
            'grid h-10 w-10 flex-none place-items-center rounded-lg border border-[#00000020] bg-prompt-safecomwhite text-prompt-safecomabbey',
            markClassName,
          )}
        >
          {mark}
        </div>
        <div className="flex min-w-0 flex-1 flex-col">
          <h3 className="font-semibold text-prompt-safecomvulcan">{title}</h3>
          <p className="text-sm text-prompt-safecomabbey">{subtitle}</p>
        </div>
        {aside}
      </div>
      {children}
    </div>
  );
}

function GuideLink({ onClick }: { onClick: () => void }) {
  return (
    <button
      type="button"
      onClick={onClick}
      className="inline-flex flex-none items-center gap-1 text-sm font-medium text-prompt-safecomoutrageous-orange hover:underline"
    >
      <BookOpen className="h-3.5 w-3.5" />
      How to connect
    </button>
  );
}
